import { generateUuid } from "./id";

type IdentityRecordBase = Record<string, unknown> & {
  uuid?: unknown;
  name?: unknown;
};

type ParsedBodyIdentity = IdentityRecordBase & {
  children?: unknown;
  joints?: unknown;
  sites?: unknown;
  geoms?: unknown;
};

type ParsedCameraIdentity = IdentityRecordBase & {
  targetBodyUuid?: unknown;
};

type ParsedLightIdentity = IdentityRecordBase & {
  mountBodyUuid?: unknown;
  mountSiteUuid?: unknown;
  targetBodyUuid?: unknown;
};

type ParsedActuatorIdentity = IdentityRecordBase & {
  joint?: unknown;
  tendonUuid?: unknown;
};

type ParsedTendonJointRef = IdentityRecordBase & {
  joint?: unknown;
};

type ParsedTendonIdentity = IdentityRecordBase & {
  joints?: unknown;
  spatial?: unknown;
};

type ParsedSensorIdentity = IdentityRecordBase & {
  attrs?: unknown;
};

type ParsedEqualityIdentity = IdentityRecordBase & {
  obj1?: unknown;
  obj2?: unknown;
};

type ParsedContactPairIdentity = IdentityRecordBase & {
  geom1?: unknown;
  geom2?: unknown;
};

type ParsedContactExcludeIdentity = IdentityRecordBase & {
  body1?: unknown;
  body2?: unknown;
};

type ParsedWorldBodyIdentity = { uuid?: unknown; name?: unknown } | string;
type IdentityRecord<T extends IdentityRecordBase = IdentityRecordBase> = Record<string, T>;
const _identityMapper = <T extends IdentityRecordBase>(value: T): T => value;
type IdentityMapper<T extends IdentityRecordBase> = typeof _identityMapper<T>;

export type ParsedSceneIdentityData = {
  bodies?: IdentityRecord<ParsedBodyIdentity>;
  worldBodies?: Array<{ uuid: string; name?: string } | string>;
  joints?: IdentityRecordBase[] | IdentityRecord;
  sites?: IdentityRecord;
  cameras?: IdentityRecord<ParsedCameraIdentity>;
  lights?: IdentityRecord<ParsedLightIdentity>;
  actuators?: IdentityRecord<ParsedActuatorIdentity>;
  tendons?: IdentityRecord<ParsedTendonIdentity>;
  sensors?: IdentityRecord<ParsedSensorIdentity>;
  // Parsed import payloads use `.equalities`; current scene state stores the same domain as `.equality`.
  equalities?: ParsedEqualityIdentity[] | IdentityRecord<ParsedEqualityIdentity>;
  contactPairs?: ParsedContactPairIdentity[];
  contactExcludes?: ParsedContactExcludeIdentity[];
};

const toId = (value: unknown): string | null => {
  if (typeof value !== "string" && typeof value !== "number") return null;
  const text = String(value).trim();
  return text ? text : null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const identityValues = <T extends IdentityRecordBase>(
  record: IdentityRecord<T> | T[] | undefined
): T[] => {
  if (!record) return [];
  return Array.isArray(record) ? record : Object.values(record);
};

const collectSceneIdentityIds = (scene: unknown): Set<string> => {
  const source = isRecord(scene) ? scene : {};
  const ids = new Set<string>();
  const add = (value: unknown) => {
    const id = toId(value);
    if (id) ids.add(id);
  };
  const addRecord = (record: unknown) => {
    if (!isRecord(record)) return;
    Object.entries(record).forEach(([key, value]) => {
      add(key);
      add(isRecord(value) ? value.uuid : undefined);
    });
  };

  addRecord(source.bodies);
  Object.values(isRecord(source.bodies) ? source.bodies : {}).forEach((body) => {
    const geoms = isRecord(body) && Array.isArray(body.geoms) ? body.geoms : [];
    geoms.forEach((geom) => add(isRecord(geom) ? geom.uuid : undefined));
  });
  addRecord(source.joints);
  addRecord(source.sites);
  addRecord(source.cameras);
  addRecord(source.lights);
  addRecord(source.actuators);
  addRecord(source.tendons);
  addRecord(source.sensors);
  addRecord(source.equality);
  (Array.isArray(source.contactPairs) ? source.contactPairs : []).forEach((pair) =>
    add(isRecord(pair) ? pair.uuid : undefined)
  );
  return ids;
};

const remapMaybeId = <T>(value: T, idMap: Record<string, string>): T | string => {
  const id = toId(value);
  return id && idMap[id] ? idMap[id] : value;
};

const remapIdentityRecord = <
  T extends IdentityRecordBase,
  TInput extends IdentityRecord<T> | T[] | undefined,
>(
  input: TInput,
  idMap: Record<string, string>,
  mapValue: IdentityMapper<T>
): TInput => {
  if (!input) return input;
  if (Array.isArray(input)) {
    return input.map((value) => mapValue(value)) as TInput;
  }
  const next: IdentityRecord<T> = {};
  Object.entries(input).forEach(([key, value]) => {
    const mappedValue = mapValue(value);
    const mappedKey = idMap[key] || toId(mappedValue?.uuid) || key;
    next[mappedKey] = mappedValue;
  });
  return next as TInput;
};

/**
 * Merge/import creates a new scene instance in an existing scene. Preserve parsed
 * UUIDs unless they collide with an existing identity, then remap the imported
 * identity graph as a unit so references stay internal to the new copy.
 */
export function remapParsedDataIdentityCollisions<T extends ParsedSceneIdentityData>(
  parsedData: T,
  currentScene: unknown
): { parsedData: T; idMap: Record<string, string>; remapped: boolean } {
  const occupiedIds = collectSceneIdentityIds(currentScene);
  const idMap: Record<string, string> = {};

  const allocate = (rawValue: unknown): string | null => {
    const id = toId(rawValue);
    if (!id) return null;
    if (idMap[id]) return idMap[id];
    if (!occupiedIds.has(id)) {
      occupiedIds.add(id);
      return id;
    }
    let next = generateUuid();
    while (occupiedIds.has(next)) {
      next = generateUuid();
    }
    occupiedIds.add(next);
    idMap[id] = next;
    return next;
  };

  Object.entries(parsedData.bodies || {}).forEach(([key, body]) => {
    const finalId = allocate(body?.uuid || key);
    if (finalId && key !== finalId) idMap[key] = finalId;
    (Array.isArray(body?.geoms) ? body.geoms : []).forEach((geom) =>
      allocate(isRecord(geom) ? geom.uuid : undefined)
    );
  });
  identityValues(parsedData.joints).forEach((joint) => allocate(joint?.uuid));
  identityValues(parsedData.sites).forEach((site) => allocate(site?.uuid));
  identityValues(parsedData.cameras).forEach((camera) => allocate(camera?.uuid));
  identityValues(parsedData.lights).forEach((light) => allocate(light?.uuid));
  identityValues(parsedData.actuators).forEach((actuator) => allocate(actuator?.uuid));
  identityValues(parsedData.tendons).forEach((tendon) => allocate(tendon?.uuid));
  identityValues(parsedData.sensors).forEach((sensor) => allocate(sensor?.uuid));
  identityValues(parsedData.equalities).forEach((equality) => allocate(equality?.uuid));
  (Array.isArray(parsedData.contactPairs) ? parsedData.contactPairs : []).forEach((pair) =>
    allocate(pair?.uuid)
  );

  if (Object.keys(idMap).length === 0) {
    return { parsedData, idMap, remapped: false };
  }

  const remapBody = (body: ParsedBodyIdentity): ParsedBodyIdentity => {
    if (!body) return body;
    return {
      ...body,
      uuid: remapMaybeId(body.uuid, idMap),
      children: Array.isArray(body.children)
        ? body.children.map((id) => remapMaybeId(id, idMap))
        : body.children,
      joints: Array.isArray(body.joints)
        ? body.joints.map((id) => remapMaybeId(id, idMap))
        : body.joints,
      sites: Array.isArray(body.sites)
        ? body.sites.map((id) => remapMaybeId(id, idMap))
        : body.sites,
      geoms: Array.isArray(body.geoms)
        ? body.geoms.map((geom) =>
            isRecord(geom)
              ? {
                  ...geom,
                  uuid: remapMaybeId(geom.uuid, idMap),
                  pairedGeomUUID: remapMaybeId(geom.pairedGeomUUID, idMap),
                }
              : geom
          )
        : body.geoms,
    };
  };

  const remapJoint = <TValue extends IdentityRecordBase>(joint: TValue): TValue =>
    joint ? ({ ...joint, uuid: remapMaybeId(joint.uuid, idMap) } as TValue) : joint;
  const remapSite = <TValue extends IdentityRecordBase>(site: TValue): TValue =>
    site ? ({ ...site, uuid: remapMaybeId(site.uuid, idMap) } as TValue) : site;
  const remapCamera = (camera: ParsedCameraIdentity): ParsedCameraIdentity =>
    camera
      ? {
          ...camera,
          uuid: remapMaybeId(camera.uuid, idMap),
          targetBodyUuid: remapMaybeId(camera.targetBodyUuid, idMap),
        }
      : camera;
  const remapLight = (light: ParsedLightIdentity): ParsedLightIdentity =>
    light
      ? {
          ...light,
          uuid: remapMaybeId(light.uuid, idMap),
          mountBodyUuid: remapMaybeId(light.mountBodyUuid, idMap),
          mountSiteUuid: remapMaybeId(light.mountSiteUuid, idMap),
          targetBodyUuid: remapMaybeId(light.targetBodyUuid, idMap),
        }
      : light;
  const remapActuator = (actuator: ParsedActuatorIdentity): ParsedActuatorIdentity =>
    actuator
      ? {
          ...actuator,
          uuid: remapMaybeId(actuator.uuid, idMap),
          joint: remapMaybeId(actuator.joint, idMap),
          tendonUuid: remapMaybeId(actuator.tendonUuid, idMap),
        }
      : actuator;
  const remapTendon = (tendon: ParsedTendonIdentity): ParsedTendonIdentity =>
    tendon
      ? {
          ...tendon,
          uuid: remapMaybeId(tendon.uuid, idMap),
          joints: Array.isArray(tendon.joints)
            ? tendon.joints.map((ref) =>
                isRecord(ref)
                  ? ({ ...ref, joint: remapMaybeId(ref.joint, idMap) } as ParsedTendonJointRef)
                  : ref
              )
            : tendon.joints,
          spatial: Array.isArray(tendon.spatial)
            ? tendon.spatial.map((ref) =>
                isRecord(ref)
                  ? {
                      ...ref,
                      site: remapMaybeId(ref.site, idMap),
                      sidesite: remapMaybeId(ref.sidesite, idMap),
                      geom: remapMaybeId(ref.geom, idMap),
                    }
                  : ref
              )
            : tendon.spatial,
        }
      : tendon;
  const remapSensorReferenceFields = (sensor: ParsedSensorIdentity): ParsedSensorIdentity => {
    if (!sensor) return sensor;
    const next: ParsedSensorIdentity = { ...sensor, uuid: remapMaybeId(sensor.uuid, idMap) };
    [
      "body",
      "bodyUuid",
      "joint",
      "jointUuid",
      "site",
      "siteUuid",
      "tendon",
      "tendonUuid",
      "actuator",
      "actuatorUuid",
      "geom",
      "geomUuid",
      "obj1",
      "obj2",
    ].forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(sensor, key)) {
        next[key] = remapMaybeId(sensor[key], idMap);
      }
    });
    if (isRecord(sensor.attrs)) {
      next.attrs = Object.fromEntries(
        Object.entries(sensor.attrs).map(([key, value]) => [key, remapMaybeId(value, idMap)])
      );
    }
    return next;
  };
  const remapEquality = (equality: ParsedEqualityIdentity): ParsedEqualityIdentity =>
    equality
      ? {
          ...equality,
          uuid: remapMaybeId(equality.uuid, idMap),
          obj1: remapMaybeId(equality.obj1, idMap),
          obj2: remapMaybeId(equality.obj2, idMap),
        }
      : equality;

  const nextParsedData = {
    ...parsedData,
    bodies: remapIdentityRecord(parsedData.bodies, idMap, remapBody) as T["bodies"],
    worldBodies: Array.isArray(parsedData.worldBodies)
      ? parsedData.worldBodies.map((worldBody: ParsedWorldBodyIdentity) =>
          typeof worldBody === "string"
            ? remapMaybeId(worldBody, idMap)
            : { ...worldBody, uuid: remapMaybeId(worldBody?.uuid, idMap) }
        )
      : parsedData.worldBodies,
    joints: remapIdentityRecord(parsedData.joints, idMap, remapJoint) as T["joints"],
    sites: remapIdentityRecord(parsedData.sites, idMap, remapSite) as T["sites"],
    cameras: remapIdentityRecord(parsedData.cameras, idMap, remapCamera) as T["cameras"],
    lights: remapIdentityRecord(parsedData.lights, idMap, remapLight) as T["lights"],
    actuators: remapIdentityRecord(parsedData.actuators, idMap, remapActuator) as T["actuators"],
    tendons: remapIdentityRecord(parsedData.tendons, idMap, remapTendon) as T["tendons"],
    sensors: remapIdentityRecord(
      parsedData.sensors,
      idMap,
      remapSensorReferenceFields
    ) as T["sensors"],
    equalities: remapIdentityRecord(parsedData.equalities, idMap, remapEquality) as T["equalities"],
    contactPairs: Array.isArray(parsedData.contactPairs)
      ? parsedData.contactPairs.map((pair) =>
          pair
            ? {
                ...pair,
                uuid: remapMaybeId(pair.uuid, idMap),
                geom1: remapMaybeId(pair.geom1, idMap),
                geom2: remapMaybeId(pair.geom2, idMap),
              }
            : pair
        )
      : parsedData.contactPairs,
    contactExcludes: Array.isArray(parsedData.contactExcludes)
      ? parsedData.contactExcludes.map((exclude) =>
          exclude
            ? {
                ...exclude,
                body1: remapMaybeId(exclude.body1, idMap),
                body2: remapMaybeId(exclude.body2, idMap),
              }
            : exclude
        )
      : parsedData.contactExcludes,
  } as T;

  return { parsedData: nextParsedData, idMap, remapped: true };
}
